import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Loader2, CheckCircle, XCircle } from 'lucide-react'
import { supabase } from '@/lib/supabase'

type CallbackType = 'supabase' | 'google-business' | 'unknown'

export function AuthCallbackPage() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing')
  const [message, setMessage] = useState(t('auth.callbackCompleting'))

  useEffect(() => {
    async function processCallback() {
      // Determine callback type
      const callbackType = detectCallbackType()

      if (callbackType === 'supabase') {
        await handleSupabaseCallback()
      } else if (callbackType === 'google-business') {
        await handleGoogleBusinessCallback()
      } else {
        setStatus('error')
        setMessage(t('auth.callbackUnknown'))
        setTimeout(() => navigate('/login'), 3000)
      }
    }

    function detectCallbackType(): CallbackType {
      // Supabase auth uses hash-based tokens for magic links and email confirmation
      const hashParams = new URLSearchParams(window.location.hash.substring(1))
      const hasAccessToken = hashParams.has('access_token')
      const hasType = hashParams.has('type')

      if (hasAccessToken || hasType) {
        return 'supabase'
      }

      // Google Business Profile OAuth uses query params
      const success = searchParams.get('success')
      const userId = searchParams.get('user_id')

      if (success !== null || userId !== null) {
        return 'google-business'
      }

      // Check for Supabase error in query params (email confirmation errors)
      const errorDescription = searchParams.get('error_description')
      if (errorDescription) {
        return 'supabase'
      }

      return 'unknown'
    }

    async function handleSupabaseCallback() {
      try {
        // Check for error in URL (e.g., expired link)
        const errorDescription = searchParams.get('error_description')
        if (errorDescription) {
          setStatus('error')
          setMessage(errorDescription)
          setTimeout(() => navigate('/login'), 3000)
          return
        }

        // Parse hash params for Supabase auth
        const hashParams = new URLSearchParams(window.location.hash.substring(1))
        const type = hashParams.get('type')

        // For email confirmation, Supabase automatically signs in the user
        // The auth state change listener will pick up the session
        const {
          data: { session },
          error,
        } = await supabase.auth.getSession()

        if (error) {
          throw error
        }

        if (session) {
          if (type === 'signup') {
            setStatus('success')
            setMessage(t('auth.callbackEmailConfirmed'))
          } else if (type === 'magiclink') {
            setStatus('success')
            setMessage(t('auth.callbackSignedIn'))
          } else {
            setStatus('success')
            setMessage(t('auth.callbackAuthSuccess'))
          }

          // Redirect to onboarding or dashboard
          setTimeout(() => navigate('/onboarding'), 1500)
        } else {
          // No session but no error - might be processing
          // Wait for auth state change
          const timeout = setTimeout(() => {
            setStatus('error')
            setMessage(t('auth.callbackTimeout'))
            navigate('/login')
          }, 5000)

          const {
            data: { subscription },
          } = supabase.auth.onAuthStateChange((_event, newSession) => {
            if (newSession) {
              clearTimeout(timeout)
              subscription.unsubscribe()
              setStatus('success')
              setMessage(t('auth.callbackSignedIn'))
              setTimeout(() => navigate('/onboarding'), 1500)
            }
          })
        }
      } catch (err) {
        setStatus('error')
        setMessage(err instanceof Error ? err.message : t('auth.callbackFailed'))
        setTimeout(() => navigate('/login'), 3000)
      }
    }

    async function handleGoogleBusinessCallback() {
      const success = searchParams.get('success')
      const error = searchParams.get('error')

      if (success === 'false' || error) {
        setStatus('error')
        setMessage(error || t('auth.callbackFailed'))
        setTimeout(() => {
          navigate('/onboarding?error=' + encodeURIComponent(error || 'oauth_failed'))
        }, 3000)
        return
      }

      if (success === 'true') {
        const userId = searchParams.get('user_id')
        const email = searchParams.get('email')
        const accountsSynced = searchParams.get('accounts_synced')

        // Store user info in sessionStorage (temporary - will be replaced by Supabase session)
        if (userId) sessionStorage.setItem('user_id', userId)
        if (email) sessionStorage.setItem('email', email)
        if (accountsSynced) sessionStorage.setItem('accounts_synced', accountsSynced)

        setStatus('success')
        setMessage(t('auth.callbackAccountsSynced', { count: Number(accountsSynced) || 0 }))

        // Redirect to onboarding to select locations
        setTimeout(() => {
          navigate('/onboarding?oauth=success')
        }, 1500)
      }
    }

    processCallback()
  }, [searchParams, navigate, t])

  return (
    <div className="min-h-screen bg-warm-gray flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-white rounded-2xl border border-border p-8 text-center space-y-6">
        {status === 'processing' && (
          <>
            <Loader2 className="w-16 h-16 text-primary animate-spin mx-auto" />
            <h1 className="text-2xl font-bold text-foreground">{t('auth.callbackProcessing')}</h1>
            <p className="text-muted-foreground">{message}</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mx-auto">
              <CheckCircle className="w-10 h-10 text-accent" />
            </div>
            <h1 className="text-2xl font-bold text-foreground">
              {t('auth.callbackSuccessHeading')}
            </h1>
            <p className="text-muted-foreground">{message}</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto">
              <XCircle className="w-10 h-10 text-destructive" />
            </div>
            <h1 className="text-2xl font-bold text-foreground">
              {t('auth.callbackFailedHeading')}
            </h1>
            <p className="text-muted-foreground">{message}</p>
            <p className="text-sm text-muted-foreground">{t('auth.callbackRedirecting')}</p>
          </>
        )}
      </div>
    </div>
  )
}
